<template>
    <div class="note-confirm-card">
        <div class="card-header">
            <span class="card-icon" aria-hidden="true">📝</span>
            <div class="card-title">
                <div class="title-main">{{ $t('AssistantPanel.NoteDraftTitle') }}</div>
                <div v-if="remindText" class="title-sub">⏰ {{ remindText }}</div>
            </div>
        </div>

        <dl class="card-fields">
            <div class="field-row">
                <dt>{{ $t('Note') }}</dt>
                <dd class="note-text">{{ draft.text }}</dd>
            </div>
            <div v-if="remindText" class="field-row">
                <dt>{{ $t('RemindDate') }}</dt>
                <dd>{{ remindText }}</dd>
            </div>
            <div v-if="draft.staffName" class="field-row">
                <dt>{{ $t('Staff') }}</dt>
                <dd>{{ draft.staffName }}</dd>
            </div>
        </dl>

        <div class="card-actions">
            <button
                type="button"
                class="btn btn-primary"
                :disabled="!draft.canCreate || saving"
                @click="onConfirm">
                <span v-if="!saving">✓ {{ $t('Create') }}</span>
                <span v-else class="spinner" aria-hidden="true"></span>
            </button>
            <button
                type="button"
                class="btn btn-secondary"
                :disabled="saving"
                @click="$emit('edit')">
                ✏ {{ $t('Edit') }}
            </button>
            <button
                type="button"
                class="btn-ghost"
                :disabled="saving"
                @click="$emit('cancel')">
                {{ $t('Cancel') }}
            </button>
        </div>

        <div v-if="errorMsg" class="card-error">
            <span class="error-icon" aria-hidden="true">!</span>
            <span>{{ errorMsg }}</span>
        </div>
    </div>
</template>

<script>
    import axios from '../../services/index';

    const DAY_NAMES = ['Pazar', 'Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi'];
    const MONTH_NAMES = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
                         'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];

    export default {
        name: 'NoteConfirmCard',
        props: {
            draft: { type: Object, required: true },
            // Cevabı üreten AI çağrısının log id'si → Customer_Note.AiCallLogId (AI izi).
            aiCallLogId: { type: Number, default: null }
        },
        data() {
            return {
                saving: false,
                errorMsg: ''
            };
        },
        computed: {
            remindText() {
                const p = this.draft.upsertPayload;
                if (!p || !p.remindDate) return '';
                const d = new Date(p.remindDate);
                if (isNaN(d.getTime())) return '';
                const dateText = `${d.getDate()} ${MONTH_NAMES[d.getMonth()]} ${d.getFullYear()} ${DAY_NAMES[d.getDay()]}`;
                return p.time ? `${dateText} ${p.time}` : dateText;
            }
        },
        methods: {
            async onConfirm() {
                if (!this.draft.canCreate || this.saving) return;
                this.saving = true;
                this.errorMsg = '';
                try {
                    const payload = Object.assign({}, this.draft.upsertPayload, {
                        aiSessionId: this.aiCallLogId || 0
                    });
                    const res = await axios.post('/CustomerNote/InsertMany', payload);
                    if (res && res.data && res.data.success !== false) {
                        this.$emit('confirm', res.data);
                    } else {
                        this.errorMsg = (res && res.data && res.data.message)
                            || this.$t('AssistantPanel.NoteCreateFailed');
                    }
                } catch (err) {
                    const apiMsg = err && err.response && err.response.data && err.response.data.message;
                    this.errorMsg = apiMsg || this.$t('AssistantPanel.NoteCreateFailed');
                } finally {
                    this.saving = false;
                }
            }
        }
    };
</script>

<style scoped>
    .note-confirm-card {
        margin-top: 10px;
        padding: 14px;
        border-radius: 14px;
        background: linear-gradient(135deg, rgba(236, 253, 245, 0.85) 0%, rgba(240, 253, 250, 0.85) 100%);
        border: 1px solid rgba(16, 185, 129, 0.22);
        box-shadow: 0 2px 10px rgba(16, 185, 129, 0.08);
    }

    /* Header */
    .card-header {
        display: flex;
        align-items: center;
        gap: 10px;
        margin-bottom: 12px;
    }
    .card-icon {
        font-size: 22px;
        line-height: 1;
        filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.08));
    }
    .card-title { line-height: 1.25; }
    .title-main { font-size: var(--est-fs-body); font-weight: 600; color: #1e293b; }
    .title-sub { font-size: var(--est-fs-meta); color: #047857; font-variant-numeric: tabular-nums; margin-top: 2px; }

    /* Fields */
    .card-fields {
        margin: 0 0 14px 0;
        padding: 0;
        display: grid;
        gap: 6px;
    }
    .field-row {
        display: flex;
        align-items: baseline;
        gap: 8px;
        font-size: var(--est-fs-meta);
    }
    .field-row dt {
        flex: 0 0 90px;
        color: #64748b;
        font-weight: 500;
    }
    .field-row dd {
        flex: 1;
        margin: 0;
        color: #1e293b;
        font-weight: 500;
    }
    .note-text { white-space: pre-wrap; }

    /* Actions */
    .card-actions {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
    }
    .btn-primary, .btn-secondary, .btn-ghost {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        padding: 7px 14px;
        border-radius: 10px;
        font-size: var(--est-fs-meta);
        font-weight: 600;
        cursor: pointer;
        transition: transform 0.12s ease, box-shadow 0.15s ease, opacity 0.15s ease;
    }
    .btn-primary:hover:not(:disabled) {
        transform: translateY(-1px);
    }

    .btn-ghost {
        border: 1px solid transparent;
        background: transparent;
        color: #64748b;
        border-color: rgba(100, 116, 139, 0.20);
    }
    .btn-ghost:hover:not(:disabled) {
        background: rgba(100, 116, 139, 0.08);
    }
    .btn-ghost:disabled { opacity: 0.4; cursor: not-allowed; }

    /* Spinner */
    .spinner {
        width: 13px;
        height: 13px;
        border: 2px solid rgba(255, 255, 255, 0.4);
        border-top-color: white;
        border-radius: 50%;
        animation: spin 0.7s linear infinite;
    }
    @keyframes spin { to { transform: rotate(360deg); } }

    /* Error */
    .card-error {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-top: 10px;
        padding: 7px 10px;
        background: rgba(254, 226, 226, 0.65);
        border: 1px solid rgba(239, 68, 68, 0.25);
        border-radius: 8px;
        font-size: var(--est-fs-meta);
        color: #991b1b;
    }
    .error-icon {
        width: 16px;
        height: 16px;
        border-radius: 50%;
        background: #ef4444;
        color: white;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        font-size: 10px;
        font-weight: 700;
        flex-shrink: 0;
    }

    /* Mobile */
    @media (max-width: 768px) {
        .note-confirm-card { padding: 12px; }
        .field-row dt { flex-basis: 80px; font-size: var(--est-fs-meta); }
        .field-row dd { font-size: var(--est-fs-meta); }
        .btn-primary, .btn-secondary, .btn-ghost { font-size: var(--est-fs-meta); padding: 6px 12px; }
    }
</style>
